import type { DeliveryState, DeliveryTableData } from './types'
import type { UseMutationOptions, UseMutationResult } from '@tanstack/react-query'

import { useMutation, useQuery } from '@tanstack/react-query'
import toast from 'react-hot-toast'

import http from '../../../../../lib/http'
import { queryClient } from '../../../../../lib/query-client'

function normalizeDeliveryHistory (data: DeliveryState): Record<string, unknown> {
  return (Object.keys(data) as Array<keyof DeliveryState>).reduce<Record<string, unknown>>((acc, key) => {
    if (typeof data[key] === 'string' && (data[key]?.length ?? 0) <= 0) {
      return {
        ...acc
      }
    }

    if (!data[key]) {
      return {
        ...acc
      }
    }

    return {
      ...acc,
      [key]: data[key] ?? null
    }
  }, {})
}


export function useDeliveryHistory (patientId?: string) {
  return useQuery({
    queryKey: [ 'delivery-history', patientId ?? '' ],
    async queryFn () {
      if (!patientId) {
        return
      }

      return await http.getRequest<DeliveryTableData[]>(`/patients/${patientId}/maternity/delivery-history`)
    },
    refetchOnWindowFocus: false
  })
}

export function useDeliverySubmitMutation (patientId?: string): UseMutationResult<unknown, unknown, DeliveryState, unknown> {
  return useMutation<unknown, unknown, DeliveryState, unknown>({
    async mutationFn (deliveryHistory) {
      if (!patientId) {
        return
      }

      const data = normalizeDeliveryHistory(deliveryHistory)

      return await http.postRequest(`/patients/${patientId}/maternity/delivery-history`, data)
    },
    onSuccess () {
      invalidateDeliveryHistory(patientId ?? '')
      toast.success('Delivery record successfully created', { position: 'bottom-center' })
    },
    onError () {
      toast.error('Something went wrong while saving', { position: 'bottom-center' })
    }
  })
}

type UseDeliveryDeleteMutationOptions = UseMutationOptions<unknown, unknown, { id: string }, unknown>
type UseDeliveryDeleteMutationResult = UseMutationResult<unknown, unknown, { id: string }, unknown>

export function useDeliveryDeleteMutation (patientId: string, options: UseDeliveryDeleteMutationOptions): UseDeliveryDeleteMutationResult {
  return useMutation<unknown, unknown, { id: string }, unknown>({
    async mutationFn ({ id }) {
      if (!id) {
        return
      }

      return await http.deleteRequest(`/patients/${patientId}/maternity/delivery-history/${id}`)
    },
    onSuccess () {
      invalidateDeliveryHistory(patientId ?? '')
      toast.success('Delivery record successfully deleted', { position: 'bottom-center' })
    },
    onError () {
      toast.error('Something went wrong while deleting', { position: 'bottom-center' })
    },
    ...options
  })
}

export function useDeliveryUpdateMutation (patientId?: string): UseMutationResult<unknown, unknown, { id: string, data: DeliveryState }, unknown> {
  return useMutation<unknown, unknown, { id: string, data: DeliveryState }, unknown>({
    async mutationFn ({ id, data: deliveryHistory }) {
      if (!id || !patientId) {
        return
      }

      const data = normalizeDeliveryHistory(deliveryHistory)

      return await http.putRequest(`/patients/${patientId}/maternity/delivery-history/${id}`, data)
    },
    onSuccess () {
      invalidateDeliveryHistory(patientId ?? '')
      toast.success('Delivery record successfully updated', { position: 'bottom-center' })
    },
    onError () {
      toast.error('Something went wrong while updating', { position: 'bottom-center' })
    }
  })
}

export function invalidateDeliveryHistory (patientId: string): void {
  queryClient.invalidateQueries({ queryKey: [ 'delivery-history', patientId ?? '' ] })
    .catch((e) => {
      console.error(e)
    })
}
